import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useBatchId } from '../../hooks/useBatchId';
import {
	APPLICANT_HOME,
	SELECT_MODULE,
	SELECT_ROLE,
} from '../../shared/Routes';
import { UserRole } from '../../shared/VolunteerApplicationServiceTypes';
import useUserSession from '../../hooks/useUserSession';
import {
	getLoggedInUserRoles,
	getRoleModulesMapping,
} from '../../service/RoleBasedService';
import { useToastMessage } from '../../hooks/useToastMessage';
import { Button, Layout } from 'antd';
import { DisplayRole } from '../../shared/Constants';
import StackedStones from '../../assets/images/stacked-stones.png';
import LogoutImage from '../../assets/images/logout-red-icon.svg';
import Icon from '../../components/ui-components/Icon';
import { displayError } from '../../shared/Utils';
import { useLocation } from 'react-router-dom';

type RoleButton = {
	key: string;
	label: string;
	onClick: (roleCode: string) => void;
};

type LocationState = {
	fromSelfRegistration?: boolean;
};

const SelectRole: FC = () => {
	const { t } = useTranslation();
	const { navigateToRoute } = useBatchId();
	const {
		loggedInUserRoles,
		selectedRole,
		setAllRolesWithModules,
		setLoggedInUserRoles,
		setSelectedRole,
	} = useUserSession();
	const { initiated, isLoggedIn, srcmId, logout, mySrcmProfile } = useAuth();
	const location = useLocation() as { state: LocationState };
	const [isLoadingRoles, setIsLoadingRoles] = useState(false);
	const [rolesError, setRolesError] = useState<string | null>(null);
	const { showToastMessage } = useToastMessage();
	const roleName = `${mySrcmProfile?.first_name || ''} ${
		mySrcmProfile?.middle_name || ''
	} ${mySrcmProfile?.last_name || ''}`;
	const roleCodes = loggedInUserRoles
		? Array.from(
				new Set(loggedInUserRoles.map((item) => item.role.code))
		  ).sort()
		: [];
	const isFromSelfRegistration = location.state?.fromSelfRegistration || false;
	const selfRegistrationRoles = [UserRole.PROPOSER, UserRole.PREWORK_MENTOR];

	const existingRoleSet = new Set(
		(roleCodes ?? []).map((r) => r?.toUpperCase())
	);

	const hasAllSelfRegistrationRoles = selfRegistrationRoles.every((role) =>
		existingRoleSet.has(role)
	);

	const handleLogoutClick = useCallback(() => {
		if (logout) {
			void logout();
		}
	}, [logout]);

	const fetchUserRoles = useCallback(async () => {
		if (!srcmId) return;

		setIsLoadingRoles(true);
		setRolesError(null);

		try {
			const roleResponse = await getRoleModulesMapping({
				loggedInFunctionarySrcmId: srcmId,
				filters: {},
			});

			if (!roleResponse?.roleModulesMappings) {
				throw new Error('Unable to fetch role-module mapping');
			}

			setAllRolesWithModules(roleResponse.roleModulesMappings);

			const userRoles = await getLoggedInUserRoles({
				loggedInUserSrcmId: srcmId,
			});

			if (!userRoles?.rolesDetails) {
				throw new Error('Unable to fetch user roles');
			}
			if (userRoles.isApplicant) {
				setSelectedRole(UserRole.APPLICANT);
				navigateToRoute(APPLICANT_HOME);
				return;
			}

			setLoggedInUserRoles(userRoles.rolesDetails);
		} catch (e) {
			const message = displayError(e);
			setRolesError(message);
			showToastMessage('error', message);
		} finally {
			setIsLoadingRoles(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [srcmId]);

	useEffect(() => {
		if (initiated) {
			if (isLoggedIn && isLoggedIn() && selectedRole !== UserRole.APPLICANT) {
				fetchUserRoles().catch((e) => {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				});
			} else {
				navigateToRoute(APPLICANT_HOME);
			}
		} else {
			const oneAuthElement = document.querySelector(
				'hfn-auth'
			) as HTMLElement & { triggerAuth?: () => void };

			if (oneAuthElement?.triggerAuth) {
				oneAuthElement.triggerAuth();
			}
		}
	}, [
		fetchUserRoles,
		initiated,
		isLoggedIn,
		selectedRole,
		navigateToRoute,
		showToastMessage,
	]);

	const handleRoleClick = useCallback(
		(roleCode: string) => {
			setSelectedRole(roleCode as UserRole);
			navigateToRoute(SELECT_MODULE);
		},
		[navigateToRoute, setSelectedRole]
	);

	const roleButtons: RoleButton[] = roleCodes.map((roleCode) => {
		return {
			key: roleCode,
			label: DisplayRole[roleCode as keyof typeof DisplayRole],
			onClick: handleRoleClick,
		};
	});

	return (
		<>
			<Layout
				data-testid="select-role-page"
				className="bg-bground-blue flex items-center py-10"
			>
				<div className="bg-white rounded-2xl md:shadow-md w-full max-w-5xl h-full max-h-[85vh] flex flex-col md:flex-row overflow-hidden">
					{/* Left Image Section - Desktop Only */}
					<div className="hidden md:flex md:w-1/2 p-8 justify-center items-center">
						<img
							src={StackedStones}
							alt="Self-Nominating Preceptor"
							className="max-h-full max-w-full rounded-2xl"
						/>
					</div>

					{/* Right Content */}
					<div className="flex flex-col items-center justify-center w-full md:w-1/2 p-6 md:p-10">
						<h1 className="text-2xl md:text-3xl font-bold font-sans mb-2 text-center">
							{t('welcomeRCAC', { roleName })}
						</h1>
						<p className="font-poppins font-medium text-gray-650 mb-6 text-center">
							{t('pleaseSelectARole')}
						</p>

						<div className="flex flex-col gap-4 w-[80%]">
							{isLoadingRoles && (
								<p className="text-gray-500 text-center font-medium">
									{t('loadingRoles')}
								</p>
							)}

							{!isLoadingRoles && rolesError && (
								<div className="text-center">
									<p className="text-red-600 font-semibold mb-2">
										{t('failedToLoadRoles')}
									</p>
									<Button
										type="default"
										onClick={() => {
											void fetchUserRoles();
										}}
										className="font-semibold"
									>
										{t('retry')}
									</Button>
								</div>
							)}
							{!isLoadingRoles &&
								!rolesError &&
								roleButtons.map(({ key, label, onClick }) => {
									return (
										<Button
											key={key}
											data-testid="select-role"
											size="large"
											type="primary"
											className="w-full h-[50px] font-sans font-semibold text-base text-gray-650 bg-white border border-solid border-gray-200 hover:bg-primary-blue shadow-none focus:shadow-none active:shadow-none"
											onClick={() => {
												onClick?.(key);
											}}
										>
											{label}
										</Button>
									);
								})}
						</div>

						{isFromSelfRegistration && !hasAllSelfRegistrationRoles && (
							<h1 className="text-sm font-semibold text-gray-600 mt-6 text-center">
								<span
									className="text-primary-blue underline cursor-pointer font-semibold"
									onClick={() =>
										navigateToRoute(SELECT_ROLE, {
											state: {
												existingRoles: roleCodes,
											},
										})
									}
								>
									{t('goToSelfRegister')}
								</span>
								.
							</h1>
						)}

						<div className="mt-6 text-center">
							<Button
								icon={
									<Icon
										src={LogoutImage}
										className="w-4 h-4 flex items-center"
									/>
								}
								type="text"
								danger
								className="font-sans font-semibold text-base h-[50px] hover:py-4 text-red-600 hover:text-red-700"
								onClick={handleLogoutClick}
							>
								{t('logout')}
							</Button>
						</div>
					</div>
				</div>
			</Layout>
		</>
	);
};

export default SelectRole;
